// PostHog product analytics — funnels, retention, session replay, autocapture,
// $initial_utm_* attribution.
//
// Gated by `VITE_POSTHOG_KEY`. Missing key → every helper is a silent no-op,
// so dev builds default to "off" until the developer sets the key locally.
// Set `VITE_POSTHOG_KEY` (+ optional `VITE_POSTHOG_HOST`, defaults to PostHog
// US cloud) in `.env.{dev,stg,prod,local}` to enable.
//
// All helpers wrap calls in try/catch — PostHog failing must never break the
// app. The PostHog SDK itself is also resilient (queues events offline,
// retries), so most errors here would be malformed input on our side.

import posthog from 'posthog-js';
import type { Profile } from '../types/profile';
import { logDebug, logError } from './loggerService';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://us.i.posthog.com';

let isInitialized = false;

/**
 * Bootstrap PostHog. Call once early in app startup (next to `InitUtm`).
 * `appName` is registered as a super-property `app` so every event from
 * every app in the suite is filterable by which app fired it — funnels
 * across `www` (landing) → `core` (signup) work via this property.
 *
 * No-op when `VITE_POSTHOG_KEY` is unset. Idempotent — second call short-
 * circuits.
 */
export function initPosthog(appName: 'core' | 'www' | 'admin' | 'chef'): void {
  if (isInitialized) return;
  if (!POSTHOG_KEY) {
    logDebug('PostHog disabled (VITE_POSTHOG_KEY not set)');
    return;
  }

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // SPA pageviews — fires on History API route changes so internal
      // navigation actually tracks (the default `'config'` only captures
      // the first load and would leave every funnel after the landing
      // screen empty).
      capture_pageview: 'history_change',
      // Mask all text/inputs in session replay by default — recipes,
      // addresses, names, prices can all be PII-adjacent. We can opt-in
      // specific fields later via the `ph-no-mask` CSS class if needed.
      session_recording: { maskAllInputs: true, maskTextSelector: '*' },
      // Person profiles only for identified users — anonymous pageviews
      // don't create person rows, saves quota and keeps the people view
      // focused on signed-in users.
      person_profiles: 'identified_only',
    });
    // Stamped on every event so cross-app queries can filter / group by
    // origin app without joining anything.
    posthog.register({ app: appName });
    isInitialized = true;
    logDebug('PostHog initialized', undefined, { appName });
  } catch (error) {
    logError(error instanceof Error ? error : new Error(String(error)), 'PostHog init failed');
  }
}

/**
 * Identify the active profile to PostHog. Call after `whoAmI()` resolves AND
 * when the user switches between profiles (consumer / chef / admin) — each
 * profile is treated as a distinct PostHog identity per the user's product
 * choice (a chef who occasionally orders is two PostHog identities, one per
 * persona; lets funnels stay persona-shaped).
 *
 * Person properties carry the parent userId (so support can correlate
 * across personas in PostHog UI) plus persona + display_name for filtering.
 * Setting `$set_once` so first-touch values like initial signup persona
 * stick even on later re-identifies.
 */
export function identifyPosthogProfile(profile: Profile): void {
  if (!isInitialized || !profile?.id) return;

  try {
    posthog.identify(
      profile.id,
      {
        user_id: profile.userId,
        persona: profile.persona,
        display_name: profile.displayName,
      },
      {
        // first-touch set
        initial_persona: profile.persona,
      },
    );
    logDebug('PostHog identified', undefined, { profileId: profile.id, persona: profile.persona });
  } catch (error) {
    logError(error instanceof Error ? error : new Error(String(error)), 'PostHog identify failed');
  }
}

/**
 * Clear the PostHog identity on logout. Subsequent events are anonymous
 * until the next `identifyPosthogProfile` call. Also resets the device id
 * so the next user on this device doesn't inherit the previous person row.
 */
export function resetPosthog(): void {
  if (!isInitialized) return;

  try {
    posthog.reset();
    logDebug('PostHog reset');
  } catch (error) {
    logError(error instanceof Error ? error : new Error(String(error)), 'PostHog reset failed');
  }
}

/**
 * Explicit event capture — use for activation-critical events that
 * autocapture might miss or render with vague selectors (Ionic's shadow
 * DOM components are noisy for autocapture). Examples: `signup_completed`,
 * `dish_published`, `order_placed`. Anonymous-safe — fires even before
 * identify, gets attributed to the user once they sign in.
 */
export function capturePosthogEvent(eventName: string, properties?: Record<string, unknown>): void {
  if (!isInitialized) return;

  try {
    posthog.capture(eventName, properties);
  } catch (error) {
    logError(error instanceof Error ? error : new Error(String(error)), 'PostHog capture failed', undefined, {
      eventName,
    });
  }
}
